import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CorrelationService {
  private currentCorrelationId = new BehaviorSubject<string | null>(null);
  private sessionCorrelationId: string | null = null;

  constructor() {
    // Generate a session-level correlation ID when service is initialized
    this.sessionCorrelationId = this.generateGuid();
    console.log(`[Correlation] Session correlation ID generated: ${this.sessionCorrelationId}`);
  }

  /**
   * Generate a new correlation GUID for a user action
   * @param actionName Optional name of the action for logging
   * @returns The generated correlation GUID
   */
  generateCorrelationId(actionName?: string): string {
    const correlationId = this.generateGuid();
    this.setCorrelationId(correlationId);
    
    if (actionName) {
      console.log(`[Correlation] New correlation ID generated for action '${actionName}': ${correlationId}`);
    } else {
      console.log(`[Correlation] New correlation ID generated: ${correlationId}`);
    }
    
    return correlationId;
  }

  /**
   * Get the current correlation ID
   * @returns Current correlation ID or null if none set
   */
  getCurrentCorrelationId(): string | null {
    return this.currentCorrelationId.value;
  }

  /**
   * Get the session-level correlation ID
   * @returns Session correlation ID
   */
  getSessionCorrelationId(): string {
    return this.sessionCorrelationId!;
  }

  /**
   * Set the current correlation ID
   * @param id The correlation ID to set
   */
  setCorrelationId(id: string): void {
    this.currentCorrelationId.next(id);
    
    // Store in session storage for persistence across page reloads
    sessionStorage.setItem('currentCorrelationId', id);
  }

  /**
   * Clear the current correlation ID
   */
  clearCorrelationId(): void {
    this.currentCorrelationId.next(null);
    sessionStorage.removeItem('currentCorrelationId');
    console.log('[Correlation] Correlation ID cleared');
  }

  /**
   * Get correlation ID observable for reactive programming
   * @returns Observable of current correlation ID
   */
  getCorrelationId$(): Observable<string | null> {
    return this.currentCorrelationId.asObservable();
  }

  /**
   * Get or create a correlation ID for HTTP requests
   * If no current correlation ID exists, generates a new one
   * @returns Correlation ID for HTTP requests
   */
  getOrCreateCorrelationId(): string {
    let correlationId = this.getCurrentCorrelationId();
    
    if (!correlationId) {
      // Check session storage first
      correlationId = sessionStorage.getItem('currentCorrelationId');
      
      if (correlationId) {
        this.currentCorrelationId.next(correlationId);
      } else {
        // Generate new correlation ID if none exists
        correlationId = this.generateCorrelationId('auto-generated');
      }
    }
    
    return correlationId;
  }

  /**
   * Start a new user action with correlation tracking
   * @param actionName Name of the user action
   * @param actionData Optional data associated with the action
   * @returns The correlation ID for this action
   */
  startUserAction(actionName: string, actionData?: any): string {
    const correlationId = this.generateCorrelationId(actionName);
    
    // Log the user action with correlation
    console.log(`[Correlation] User action started: ${actionName}`, {
      correlationId,
      timestamp: new Date().toISOString(),
      actionData,
      sessionId: this.sessionCorrelationId
    });
    
    return correlationId;
  }

  /**
   * Log an event with correlation context
   * @param message Log message
   * @param data Optional data to log
   */
  logWithCorrelation(message: string, data?: any): void {
    const correlationId = this.getCurrentCorrelationId();
    const sessionId = this.getSessionCorrelationId();
    
    console.log(`[CorrelationId: ${correlationId}] ${message}`, {
      correlationId,
      sessionId,
      timestamp: new Date().toISOString(),
      data
    });
  }

  /**
   * Generate a GUID (UUID v4)
   * @returns A new GUID string
   */
  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}